import { createApi } from '@reduxjs/toolkit/query/react';
import { getBaseApiPath } from '@/app/common/api/config';
import customFetchBase from '@/app/common/utils/customFetchBase';
import type {
  AddClusteringNodeRequest,
  AddClusteringNodeResponse,
  ClusteringDetailsResponse,
  ClusteringHistoryResponse,
  ClusteringTaskRequest,
  ClusteringTaskResponse,
  DeleteClusteringHistoryResponse,
  DeleteClusteringNodeRequest,
  DeleteClusteringNodeResponse,
  UpdateClusteringNodeRequest,
  UpdateClusteringNodeResponse
} from '@/app/KeywordResearch/types/clusteringApiTypes';

const clusteringApi = createApi({
  reducerPath: 'clusteringApi',
  baseQuery: (...args: unknown[]) =>
    customFetchBase(getBaseApiPath(), ...args),
  tagTypes: ['ClusteringHistory'],
  endpoints: build => ({
    getClusteringHistory: build.query<ClusteringHistoryResponse, void>({
      query: () => ({
        url: 'keyword_related/v1/keyword_clustering/history_clustering',
        method: 'POST'
      }),
      providesTags: ['ClusteringHistory']
    }),
    getClusteringDetails: build.query<ClusteringDetailsResponse, string>({
      query: id => ({
        url: 'keyword_related/v1/keyword_clustering/view_history_clustering',
        method: 'POST',
        body: { task_id: id }
      })
    }),
    deleteClusteringHistory: build.mutation<
      DeleteClusteringHistoryResponse,
      string
    >({
      query: id => ({
        url: 'keyword_related/v1/keyword_clustering/delete_history',
        method: 'POST',
        body: { task_id: id }
      }),
      invalidatesTags: ['ClusteringHistory']
    }),
    createClustering: build.mutation<
      ClusteringTaskResponse,
      ClusteringTaskRequest
    >({
      query: body => ({
        url: 'keyword_related/v1/keyword_clustering/cluster',
        method: 'POST',
        body
      }),
      invalidatesTags: ['ClusteringHistory']
    }),
    addClusteringNode: build.mutation<
      AddClusteringNodeResponse,
      AddClusteringNodeRequest
    >({
      query: body => ({
        url: 'keyword_related/v1/keyword_clustering/add_node',
        method: 'POST',
        body
      })
    }),
    updateClusteringNode: build.mutation<
      UpdateClusteringNodeResponse,
      UpdateClusteringNodeRequest
    >({
      query: body => ({
        url: 'keyword_related/v1/keyword_clustering/update_node',
        method: 'POST',
        body
      })
    }),
    deleteClusteringNode: build.mutation<
      DeleteClusteringNodeResponse,
      DeleteClusteringNodeRequest
    >({
      query: body => ({
        url: 'keyword_related/v1/keyword_clustering/delete_node',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useGetClusteringHistoryQuery,
  useLazyGetClusteringDetailsQuery,
  useCreateClusteringMutation,
  useDeleteClusteringHistoryMutation,
  useAddClusteringNodeMutation,
  useUpdateClusteringNodeMutation,
  useDeleteClusteringNodeMutation
} = clusteringApi;

export default clusteringApi;
